import Analytics, { AnalyticsInstance } from 'analytics';
import { SessionsTypes, UserData } from '../apis/user/types';
import googleTagManager from '@analytics/google-tag-manager';
import customerIo from '@analytics/customerio';
import intercomPlugin from '@analytics/intercom';
import { getUnixTime } from 'date-fns';
import googleAnalytics from '@analytics/google-analytics';
import { track, trackClick, init as initTracker } from '@zignaly-open/tracker';
import { isFeatureOn, isZignaly, whitelabel } from '../whitelabel';
import { Features } from '@zignaly-open/ps2-definitions';
import { Capacitor } from '@capacitor/core';

initTracker({
  baseApi: whitelabel.baseApi,
  platform: Capacitor.getPlatform(),
});

let analytics: AnalyticsInstance | null = null;
const enableTracking = isFeatureOn(Features.EnableTracking);
const trackingQueue: Array<() => void> = [];

// Initialize analytics after page load
const initializeAnalytics = () => {
  const customerIoPlugin = customerIo({
    siteId: import.meta.env.VITE_CUSTOMER_IO_SITE_ID,
  });
  const originalInitialize = customerIoPlugin.initialize;

  customerIoPlugin.initialize = (args: unknown) => {
    originalInitialize.call(customerIoPlugin, args);
    const cio = document.getElementById('cio-tracker');
    cio?.setAttribute('data-use-in-app', 'true');
  };

  analytics = Analytics({
    app: 'zignaly',
    plugins: [
      whitelabel.tools?.google_tag_manager &&
        googleTagManager({
          containerId: whitelabel.tools?.google_tag_manager,
        }),
      googleAnalytics({
        measurementIds: [import.meta.env.VITE_GA_ID],
      }),
      customerIoPlugin,
      whitelabel.tools?.intercom &&
        intercomPlugin({
          appId: whitelabel.tools?.intercom,
        }),
    ].filter(Boolean),
  });

  // Process queued events
  trackingQueue.forEach((fn) => fn());
};

// Defer analytics initialization
if (typeof jest === 'undefined' && enableTracking) {
  window.addEventListener('load', () => {
    setTimeout(initializeAnalytics, 3000);
  });
}

export const trackWithQueue = (trackFn: () => void) => {
  if (analytics) {
    trackFn();
  } else if (enableTracking) {
    trackingQueue.push(trackFn);
  }
};

export const trackNewSession = (
  userData: UserData,
  eventType: SessionsTypes,
) => {
  trackWithQueue(() => {
    try {
      const { email, userId, firstName, createdAt } = userData;
      analytics?.identify(userId, {
        email,
        name: firstName,
        created_at: getUnixTime(new Date(createdAt)),
      });

      if (eventType === SessionsTypes.Signup) {
        analytics?.track('newUser', { userId });
        twq(userId).trackVerify();
      }
    } catch (e) {
      console.error(e);
    }
  });
};

export const trackEndSession = () => {};

export const trackPage = () => {
  trackWithQueue(() => analytics?.page());
};

export const gtagEvent = (
  eventName: string,
  payload?: Record<string, unknown>,
) => {
  try {
    analytics?.track(eventName, payload || {}, {
      plugins: {
        all: false,
        'google-analytics': true,
      },
    });
  } catch (e) {
    console.error(e);
  }
};

export const trackAllocation = (
  userId: string,
  amount: string,
  coin: string,
  serviceId: string,
) => {
  trackWithQueue(() => {
    gtagEvent('invest', { amount, coin, serviceId });
    twq(userId).trackAllocation();
  });
};

export const twq = (userId: string) => {
  const twqWrapper = (eventKey: string, eventData: object = {}) => {
    if (enableTracking) {
      window.twq?.('event', eventKey, {
        contents: [{ content_id: userId }],
        ...eventData,
      });
    }
  };

  const trackVerify = () => {
    twqWrapper('tw-og0cu-og0hv');
  };

  const trackAllocationFn = () => {
    twqWrapper('tw-og0cu-og0hz');
  };

  return { trackVerify, trackAllocation: trackAllocationFn };
};

export const tzTrack: typeof track = (...args) =>
  isZignaly && enableTracking && track(...args);

export const tzTrackClick: typeof trackClick = (...args) =>
  isZignaly && enableTracking && trackClick(...args);
